import type { AdminFoodRequest, AdminFoodRequestDetail } from '@steado/admin-contract/foods';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { TONE_DOTS } from '@/components/table/tone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';

import {
  approveName,
  rejectFoodRequest,
  requestQueryOptions,
  resolveFoodRequest,
} from './food-requests-query';
import { FoodPicker } from './FoodPicker';
import type { QueueNotice } from './FoodRequestsScreen';
import { ORIGIN_FROM, SOURCE_NAMES, ago, confidenceOf, figure, outcomeOf, plural } from './request-copy';
import { ADMIN_ONLY, Card, ConfidenceBadge, KindBadge, RejectForm, SourceItem } from './review-parts';

type Panel = 'none' | 'reject' | 'different';

/**
 * A name request, approved in one click from a drawer over the queue: the
 * word people used, the food it already finds, the names that food answers
 * to, and how to add it. "As another name" is where a name request starts:
 * search, typed meals and the coach find the food, and every card still says
 * what it said.
 */
export function NameRequestDrawer({
  requestId,
  next,
  onClose,
  onOpenRequest,
  onAnswered,
}: {
  readonly requestId: string;
  readonly next: AdminFoodRequest | null;
  readonly onClose: () => void;
  readonly onOpenRequest: (id: string) => void;
  readonly onAnswered: (notice: QueueNotice) => void;
}) {
  const { api } = useAuth();
  const detail = useQuery(requestQueryOptions(api, requestId));

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="right" aria-label={detail.data?.name ?? 'Name request'}>
        {detail.isError ? (
          <>
            <SheetHeader>
              <SheetTitle>Name request</SheetTitle>
            </SheetHeader>
            <SheetBody>
              <TabError
                message={detail.error.message}
                onRetry={() => {
                  void detail.refetch();
                }}
              />
            </SheetBody>
          </>
        ) : detail.data === undefined ? (
          <>
            <SheetHeader>
              <SheetTitle>Name request</SheetTitle>
            </SheetHeader>
            <SheetBody>
              <p className="text-[13px] text-muted-foreground">Loading…</p>
            </SheetBody>
          </>
        ) : (
          <NameReview
            key={detail.data.id}
            request={detail.data}
            next={next}
            onOpenRequest={onOpenRequest}
            onClose={onClose}
            onAnswered={onAnswered}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function NameReview({
  request,
  next,
  onOpenRequest,
  onClose,
  onAnswered,
}: {
  readonly request: AdminFoodRequestDetail;
  readonly next: AdminFoodRequest | null;
  readonly onOpenRequest: (id: string) => void;
  readonly onClose: () => void;
  readonly onAnswered: (notice: QueueNotice) => void;
}) {
  const { api, can } = useAuth();
  const word = capitalise(request.name);
  const food = request.food;
  const foodName = food?.displayName ?? food?.name ?? request.foodName ?? 'its food';
  const others = request.spellings
    .filter((spelling) => normalise(spelling.name) !== normalise(request.name))
    .slice(0, 5);
  const [also, setAlso] = useState<ReadonlySet<string>>(new Set(others.map((spelling) => spelling.name)));
  const [how, setHow] = useState<'alias' | 'display_name'>('alias');
  const [panel, setPanel] = useState<Panel>('none');
  const radioName = useId();
  const whyId = useId();
  const open = request.status === 'open';

  const afterAnswer = (notice: QueueNotice) => {
    onAnswered(notice);
    if (next !== null) onOpenRequest(next.id);
    else onClose();
  };

  const add = useMutation({
    mutationFn: () =>
      approveName(api, request.id, {
        as: how,
        alsoNames: [...also].map(capitalise),
      }),
    onSuccess: (approval) => {
      afterAnswer({
        text:
          how === 'alias'
            ? `Added ${word} as a name for ${foodName}.`
            : `Every card now says ${word} instead of ${foodName}.`,
        undo: [{ batchId: approval.batchId, requestId: request.id }],
      });
    },
    retry: false,
  });

  const reject = useMutation({
    mutationFn: (reason: string) => rejectFoodRequest(api, request.id, reason),
    onSuccess: () => {
      afterAnswer({ text: `Rejected ${request.name}.`, undo: [{ batchId: null, requestId: request.id }] });
    },
    retry: false,
  });

  const point = useMutation({
    mutationFn: (target: { id: string; name: string }) =>
      resolveFoodRequest(api, request.id, { foodId: target.id }),
    onSuccess: (_result, target) => {
      afterAnswer({
        text: `Pointed ${request.name} at ${target.name}. No name was added.`,
        undo: [{ batchId: null, requestId: request.id }],
      });
    },
    retry: false,
  });

  const defaultPortion = food?.portions.find((portion) => portion.isDefault) ?? food?.portions[0];
  const why = !can.mutate
    ? ADMIN_ONLY
    : request.clashes.length > 0
      ? 'Another food already answers to this name.'
      : null;

  return (
    <>
      <SheetHeader className="gap-1">
        <SheetTitle className="text-[17px] tracking-[-0.01em]">{request.name}</SheetTitle>
        <SheetDescription className="font-sans text-xs tabular-nums">
          {plural(request.userCount, 'person', 'people')} · last seen {ago(request.lastAskedAt)}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="flex flex-col gap-5 [&>*]:shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          {open ? (
            <Badge size="sm" tone="warn">
              <span aria-hidden="true" className={cn('size-1.5 rounded-full', TONE_DOTS.warn)} />
              Open
            </Badge>
          ) : (
            <Badge size="sm" tone={request.status === 'rejected' ? 'mute' : 'ok'}>
              {request.status === 'rejected' ? 'Rejected' : 'Approved'}
            </Badge>
          )}
          <KindBadge kind="name" />
          <ConfidenceBadge level={confidenceOf(request)} />
          <span className="text-xs text-muted-foreground">{ORIGIN_FROM[request.origin]}</span>
        </div>

        {!open ? (
          <Card title="What happened">
            <div className="flex flex-col gap-2 border-t border-border px-[18px] py-3.5 text-[13px]">
              <p className="text-foreground">{outcomeOf(request)}</p>
              {request.resolvedFoodId === null ? null : (
                <Link
                  to="/foods/$foodId"
                  params={{ foodId: request.resolvedFoodId }}
                  search={{ tab: 'history' }}
                  className="self-start font-medium text-info-fg no-underline hover:underline"
                >
                  {request.resolvedFoodName ?? 'The food'}’s History
                </Link>
              )}
            </div>
          </Card>
        ) : (
          <Card title="The AI proposes">
            <div className="flex flex-col gap-3.5 border-t border-border px-[18px] py-3.5">
              <p className="max-w-[70ch] text-[13px] text-foreground">
                Add <strong className="font-medium">{word}</strong> as a name for{' '}
                <strong className="font-medium">{foodName}</strong>. Everyone who typed it already got{' '}
                {foodName}; the name was just missing.
              </p>

              {food === null ? null : (
                <div className="flex flex-col gap-0.5 rounded-lg bg-background px-3 py-2.5">
                  <Link
                    to="/foods/$foodId"
                    params={{ foodId: food.id }}
                    search={{ tab: 'details' }}
                    className="inline-flex min-h-6 items-center self-start font-medium text-foreground no-underline hover:text-destructive"
                  >
                    {foodName}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {SOURCE_NAMES[food.source]}
                    {food.isVerified ? ', verified' : ''} ·{' '}
                    <span className="font-mono text-[13px] text-foreground tabular-nums">
                      {figure(food.macrosPer100g.kcal)}
                    </span>{' '}
                    kcal per 100 g
                    {defaultPortion === undefined ? null : (
                      <>
                        {' '}
                        · 1 {defaultPortion.label} is{' '}
                        <span className="font-mono text-[13px] text-foreground tabular-nums">
                          {figure(defaultPortion.grams)}
                        </span>{' '}
                        g
                      </>
                    )}
                  </span>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-muted-foreground">Names {foodName} answers to</span>
                <div className="flex flex-wrap gap-1.5">
                  {(food?.aliases ?? []).map((alias) => (
                    <span
                      key={alias}
                      className="inline-flex h-6 items-center rounded-[10px] border border-border bg-background px-2 text-xs text-muted-foreground"
                    >
                      {alias}
                    </span>
                  ))}
                  {[word, ...[...also].map(capitalise)].map((name) => (
                    <span
                      key={name}
                      className="inline-flex h-6 items-center gap-1.5 rounded-[10px] border border-ok-fg/30 bg-ok-bg px-2 text-xs font-medium text-ok-fg"
                    >
                      {name} <span className="font-normal">new</span>
                    </span>
                  ))}
                </div>
              </div>

              {others.map((spelling) => (
                <label
                  key={spelling.name}
                  className="flex min-h-8 cursor-pointer items-start gap-2.5 text-[13px]"
                >
                  <input
                    type="checkbox"
                    checked={also.has(spelling.name)}
                    onChange={(event) => {
                      setAlso((current) => {
                        const nextSet = new Set(current);
                        if (event.target.checked) nextSet.add(spelling.name);
                        else nextSet.delete(spelling.name);
                        return nextSet;
                      });
                    }}
                    className="mt-0.5 size-4 shrink-0 accent-primary"
                  />
                  <span>
                    Also add the spelling <strong className="font-medium">{capitalise(spelling.name)}</strong>{' '}
                    <span className="text-muted-foreground">
                      (typed by {spelling.people} of the {request.userCount})
                    </span>
                  </span>
                </label>
              ))}

              <fieldset className="flex flex-col gap-2">
                <legend className="pb-2 text-xs font-medium text-muted-foreground">How to add it</legend>
                {(
                  [
                    [
                      'alias',
                      'As another name',
                      `Search, typed meals and the coach find ${foodName}. Cards still say ${foodName}.`,
                    ],
                    [
                      'display_name',
                      'As the display name',
                      `Every card says ${word} instead of ${foodName}, for everyone.`,
                    ],
                  ] as const
                ).map(([value, title, line]) => (
                  <label
                    key={value}
                    className={cn(
                      'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5',
                      how === value ? 'border-foreground' : 'border-input',
                    )}
                  >
                    <input
                      type="radio"
                      name={radioName}
                      checked={how === value}
                      onChange={() => {
                        setHow(value);
                      }}
                      className="mt-0.5 size-4 shrink-0 accent-primary"
                    />
                    <span className="flex flex-col gap-0.5">
                      <span className="text-[13px] font-medium text-foreground">{title}</span>
                      <span className="text-xs text-muted-foreground">{line}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          </Card>
        )}

        <Card title="Checks the AI ran">
          <dl>
            <CheckRow label="Another food with this name">
              {request.clashes.length === 0 ? (
                <span className="text-ok-fg">None</span>
              ) : (
                <span className="text-err-fg">{request.clashes.map((clash) => clash.name).join(', ')}</span>
              )}
            </CheckRow>
            <CheckRow label="Their logs">Unchanged, they already point at {foodName}</CheckRow>
            <CheckRow label="Their cards">Say {foodName}, with no estimated label</CheckRow>
          </dl>
        </Card>

        <Card
          title="What people wrote"
          action={
            <span className="text-xs text-muted-foreground tabular-nums">
              {request.asks.length} of {request.userCount}
            </span>
          }
        >
          <ul>
            {request.asks.slice(0, 8).map((ask, index) => (
              <li
                key={`${ask.userId}-${String(index)}`}
                className="flex items-baseline gap-3 border-t border-border px-[18px] py-2.5 text-[13px]"
              >
                <span className="min-w-0 flex-1 text-foreground">“{ask.said}”</span>
                <span className="shrink-0 text-xs text-muted-foreground">{ago(ask.createdAt)}</span>
              </li>
            ))}
          </ul>
        </Card>

        {request.sources.length === 0 ? null : (
          <Card
            title="Sources"
            action={<span className="text-xs text-muted-foreground">{request.sources.length}</span>}
          >
            <ol>
              {request.sources.map((source, index) => (
                <SourceItem
                  key={`${source.url ?? 'catalog'}-${String(index)}`}
                  source={source}
                  index={index}
                />
              ))}
            </ol>
          </Card>
        )}

        {panel === 'reject' ? (
          <Card title="Reject this name">
            <div className="border-t border-border px-[18px] py-3.5">
              <RejectForm
                canMutate={can.mutate}
                isPending={reject.isPending}
                error={reject.isError ? reject.error.message : null}
                onReject={(reason) => {
                  reject.mutate(reason);
                }}
                onCancel={() => {
                  setPanel('none');
                }}
              />
            </div>
          </Card>
        ) : null}

        {panel === 'different' ? (
          <Card title="It's a different food">
            <div className="flex flex-col gap-2 border-t border-border px-[18px] py-3.5">
              <p className="text-xs text-muted-foreground">
                Pick the food people meant by {request.name}. This closes the request; it adds no name.
              </p>
              <FoodPicker
                label="Search the catalog"
                placeholder="Chapati, phulka, paratha"
                exclude={food?.id ?? null}
                disabled={!can.mutate}
                disabledWhy={ADMIN_ONLY}
                pendingId={point.isPending ? point.variables.id : null}
                onPick={(target) => {
                  point.mutate({ id: target.id, name: target.name });
                }}
              />
              {point.isError ? (
                <p role="alert" className="text-[12.5px] font-medium text-err-fg">
                  {point.error.message}
                </p>
              ) : null}
            </div>
          </Card>
        ) : null}

        {add.isError ? (
          <p role="alert" className="text-[13px] font-medium text-err-fg">
            {add.error.message}
          </p>
        ) : null}
      </SheetBody>

      {open ? (
        <SheetFooter className="flex-col items-stretch gap-2 bg-background">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              aria-expanded={panel === 'reject'}
              onClick={() => {
                setPanel(panel === 'reject' ? 'none' : 'reject');
              }}
            >
              Reject
            </Button>
            <Button
              variant="outline"
              aria-expanded={panel === 'different'}
              onClick={() => {
                setPanel(panel === 'different' ? 'none' : 'different');
              }}
            >
              It's a different food
            </Button>
            <Button
              className="ml-auto font-semibold"
              aria-disabled={why !== null || add.isPending}
              aria-describedby={why === null ? undefined : whyId}
              onClick={() => {
                if (why === null && !add.isPending) add.mutate();
              }}
            >
              <CheckIcon aria-hidden="true" className="size-[15px]" />
              {add.isPending ? 'Adding…' : 'Add the name'}
            </Button>
          </div>
          <span id={whyId} className="self-end text-xs text-muted-foreground">
            {why ??
              (next === null
                ? 'Then closes: no more names are waiting'
                : `Then opens the next one: ${next.name}`)}
          </span>
        </SheetFooter>
      ) : null}
    </>
  );
}

function CheckRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3.5 border-t border-border px-[18px] py-2.5 text-[13px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function normalise(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}
